import {
  CatalogSearchErrorCode,
  useCatalogSearchStore,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/catalog-search-store';
import { insertAndSyncOrderEntryItems } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/insert-and-sync-order-entry-items';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { SalesRepConfig } from '@domain/configuration/types';
import { transformOrderEntryItems } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { useCallback, useEffect, useRef } from 'react';
import { useGeneratedSalesPortalCatalogSearchLazyQuery } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/sales-portal-catalog-search.generated';

const GRAPHQL_ERROR_SEARCH_QUERY_LENGTH_EXCEEDED =
  'SEARCH_QUERY_LENGTH_EXCEEDED';

interface UseCatalogSearchLoaderParams {
  /** ISO date string for the delivery date used to filter catalog results. */
  deliveryDate: string;
  /** Optional callback invoked when a catalog search completes successfully. */
  onComplete?: () => void;
  /** Optional callback invoked when a catalog search fails. */
  onError?: (code: CatalogSearchErrorCode) => void;
  /**
   * Guide-level display-config headers stamped onto inserted catalog rows so
   * the item-detail panel reads consistent values regardless of which row is
   * selected.
   */
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  /** UUID of the restaurant placing the order. */
  restaurantUUID: string;
  /** Sales rep config for syncing PriceEditor instances for catalog items. */
  salesRepConfig: SalesRepConfig | null;
  /** UUID of the supplier whose catalog is being searched. */
  supplierUUID: string;
}

/**
 * Hook for executing catalog search and managing search state.
 *
 * Uses the latestQueryRef pattern to handle race conditions since Apollo
 * doesn't support request cancellation. Only the most recent query's results
 * are applied to state.
 *
 * @param params - {@link UseCatalogSearchLoaderParams} containing restaurantUUID, supplierUUID, and deliveryDate
 * @returns Object containing the search execution function
 *
 * @example
 * ```tsx
 * const { executeCatalogSearch } = useCatalogSearchLoader({
 *   restaurantUUID,
 *   supplierUUID,
 *   deliveryDate,
 * });
 *
 * // Trigger search on Enter key press
 * const handleKeyPress = (e) => {
 *   if (e.key === 'Enter') {
 *     executeCatalogSearch(searchText);
 *   }
 * };
 * ```
 */
export const useCatalogSearchLoader = ({
  deliveryDate,
  onComplete,
  onError,
  priceEditDisplayHeaders,
  restaurantUUID,
  salesRepConfig,
  supplierUUID,
}: UseCatalogSearchLoaderParams) => {
  const onCatalogSearch = useCatalogSearchStore((s) => s.onCatalogSearch);
  const onCatalogSearchError = useCatalogSearchStore(
    (s) => s.onCatalogSearchError,
  );
  const onCatalogSearchResults = useCatalogSearchStore(
    (s) => s.onCatalogSearchResults,
  );
  const latestQueryRef = useRef<string>('');

  // Reset latestQueryRef on unmount to prevent stale refs
  useEffect(() => {
    return () => {
      latestQueryRef.current = '';
    };
  }, []);

  const searchCatalog = useGeneratedSalesPortalCatalogSearchLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const executeCatalogSearch = useCallback(
    async (searchTerm: string) => {
      // Store the latest query to handle race conditions
      latestQueryRef.current = searchTerm;
      onCatalogSearch(searchTerm);

      try {
        const result = await searchCatalog({
          variables: {
            deliveryDate,
            restaurantUUID,
            searchTerm,
            supplierUUID,
          },
        });

        // Only process results if this is still the latest query
        if (searchTerm !== latestQueryRef.current) {
          return;
        }

        const rawItems =
          result.data?.searchCatalogForOrderEntryItems?.order_entry_items ?? [];
        const transformedItems = transformOrderEntryItems(
          rawItems,
          priceEditDisplayHeaders,
        );

        onCatalogSearchResults(transformedItems);
        onComplete?.();

        // Make catalog items visible to the review table / order metrics and
        // wire up their price editors.
        insertAndSyncOrderEntryItems(
          rawItems,
          priceEditDisplayHeaders,
          salesRepConfig,
        );
      } catch (error) {
        // Only process error if this is still the latest query
        if (searchTerm !== latestQueryRef.current) {
          return;
        }

        const isQueryTooLong =
          error instanceof Error &&
          'graphQLErrors' in error &&
          Array.isArray(
            (error as { graphQLErrors: { extensions?: { code?: string } }[] })
              .graphQLErrors,
          ) &&
          (
            error as { graphQLErrors: { extensions?: { code?: string } }[] }
          ).graphQLErrors.some(
            (e) =>
              e.extensions?.code === GRAPHQL_ERROR_SEARCH_QUERY_LENGTH_EXCEEDED,
          );

        const errorCode = isQueryTooLong
          ? CatalogSearchErrorCode.QueryTooLong
          : CatalogSearchErrorCode.Network;

        onCatalogSearchError(
          error instanceof Error ? error : new Error('Catalog search failed'),
          errorCode,
        );
        onError?.(errorCode);
      }
    },
    [
      deliveryDate,
      onCatalogSearch,
      onCatalogSearchError,
      onCatalogSearchResults,
      onComplete,
      onError,
      priceEditDisplayHeaders,
      restaurantUUID,
      salesRepConfig,
      searchCatalog,
      supplierUUID,
    ],
  );

  // Cancel any in-flight catalog search by invalidating the latestQueryRef.
  // Call this when exiting catalog mode (e.g. Escape key) to prevent
  // a pending query from flashing results after mode resets to OG.
  const cancelCatalogSearch = useCallback(() => {
    latestQueryRef.current = '';
  }, []);

  return { cancelCatalogSearch, executeCatalogSearch };
};
